import orderBy from 'lodash/orderBy'

import { SortOrderKey } from '@new/__global/components/FiltersFramework'

// Natural order: "301" < "1001", "PH1" after plain digits, missing/empty unitNumber falls back to the full name.
export const NATURAL_SORT_OPTIONS: Intl.CollatorOptions = { numeric: true, sensitivity: 'base' }

export interface UnitLabel {
  name?: string
  unitNumber?: string
}

// `||` on purpose: the chart label is built the same way, so an empty unitNumber also shows the name
const getUnitLabel = ({ name, unitNumber }: UnitLabel) => unitNumber || name || ''

export const compareUnitLabels = (unit1: UnitLabel, unit2: UnitLabel) =>
  getUnitLabel(unit1).localeCompare(getUnitLabel(unit2), undefined, NATURAL_SORT_OPTIONS)

const UNIT_LABEL_SORT_KEY = 'name'

/**
 * Orders tape chart listings for `fetchListings`. Grouping keys (`maxOccupancy`, room type name)
 * come first; when the user sorts by the unit label, the tiebreak is natural/numeric so that
 * room 301 precedes room 1001. Other header-driven keys keep lodash's default compare.
 */
export const sortListingResults = <
  T extends UnitLabel & { maxOccupancy?: number; group?: { name?: string } },
>(
  results: T[],
  sortBy: string,
  sortOrder: SortOrderKey,
): T[] => {
  if (sortBy !== UNIT_LABEL_SORT_KEY) {
    return orderBy(results, ['maxOccupancy', 'group.name', sortBy], [sortOrder])
  }

  const direction = sortOrder === SortOrderKey.descending ? -1 : 1
  const byUnitLabel = results.toSorted(
    (unit1, unit2) => direction * compareUnitLabels(unit1, unit2),
  )

  // lodash orderBy is stable, so the natural unit order survives as the within-group tiebreak
  return orderBy(byUnitLabel, ['maxOccupancy', 'group.name'], [sortOrder])
}
